/**
 * Early receiver-transform installation.
 *
 * Since Chrome ~141, assigning `RTCRtpReceiver.transform` only takes effect
 * when the assignment happens before the receiver's VideoReceiveStream has
 * settled and frames started flowing. In practice that means the transform
 * must be installed synchronously right after `setRemoteDescription` resolves,
 * before the local answer is created. Neither LiveKit's `TrackSubscribed`
 * event nor the peer connection `track` event fires early enough anymore —
 * frames then keep flowing to the decoder and are silently dropped there.
 *
 * To hit that window we wrap `RTCPeerConnection.prototype.setRemoteDescription`
 * (the only JS-observable point that is early enough) and notify registered
 * handlers synchronously when an SRD resolves. The wrapper is installed lazily
 * on first registration and handlers are scoped to LiveKit-track m-lines, so
 * peer connections unrelated to a LiveKit room are not affected.
 *
 * @internal
 * @packageDocumentation
 */

/**
 * Handler invoked synchronously right after an `RTCPeerConnection`'s
 * `setRemoteDescription` resolves, before the local answer is created.
 * @internal
 */
export type EarlyReceiverHandler = (
  peerConnection: RTCPeerConnection,
  description: RTCSessionDescriptionInit,
) => void;

/** @internal */
const earlyReceiverHandlers = new Set<EarlyReceiverHandler>();

/** @internal */
let srdPatched = false;

/**
 * Wrap `RTCPeerConnection.prototype.setRemoteDescription` once.
 * @internal
 */
function ensureSrdPatched(): void {
  if (srdPatched) return;
  if (typeof RTCPeerConnection === 'undefined') return;

  srdPatched = true;

  const originalSetRemoteDescription =
    RTCPeerConnection.prototype.setRemoteDescription as (
      this: RTCPeerConnection,
      description: RTCSessionDescriptionInit,
    ) => Promise<void>;

  RTCPeerConnection.prototype.setRemoteDescription = async function (
    this: RTCPeerConnection,
    description: RTCSessionDescriptionInit,
  ): Promise<void> {
    const result = await originalSetRemoteDescription.call(this, description);

    // Notify handlers synchronously, so a receiver transform can be
    // installed before the caller creates the local answer.
    for (const handler of earlyReceiverHandlers) {
      try {
        handler(this, description);
      } catch {
        // Ignore handler errors - SRD must not fail because of us.
      }
    }

    return result;
  };
}

/**
 * Register a handler that runs synchronously right after every
 * `setRemoteDescription` call on the page resolves.
 *
 * @returns Unregister function
 * @internal
 */
export function registerEarlyReceiverHandler(
  handler: EarlyReceiverHandler,
): () => void {
  ensureSrdPatched();
  earlyReceiverHandlers.add(handler);
  return () => {
    earlyReceiverHandlers.delete(handler);
  };
}

/**
 * Extract the LiveKit server track SID (`TR_...`) for a given mid from an
 * SDP, based on the `a=msid:PA_xxx|TR_yyy TR_yyy` attribute LiveKit uses.
 *
 * Returns null when the m-line has no LiveKit-style msid, which is also how
 * we tell LiveKit media apart from unrelated peer connections on the page.
 *
 * @internal
 */
export function findLiveKitTrackSid(
  sdp: string | undefined,
  mid: string | null,
): string | null {
  if (!sdp || mid === null) return null;

  // Find the m-line section for this mid (SDP lines end with \r\n)
  const sections = sdp.split(/(?=^m=)/m);
  const section = sections.find(
    (s) => s.startsWith('m=') && new RegExp(`^a=mid:${mid}\r?$`, 'm').test(s),
  );
  if (!section) return null;

  const msidMatch = section.match(/^a=msid:(.+)$/m);
  if (!msidMatch) return null;

  // LiveKit msid format: "PA_<participantSid>|<trackSid> <trackSid>"
  const trackSidMatch = msidMatch[1].match(/(TR_[A-Za-z0-9]+)/);
  return trackSidMatch ? trackSidMatch[1] : null;
}
